import React, 
       { useState, 
         useEffect,
         useCallback 
       }                                      from 'react';
import { Box,
         Button,
         Dialog,
         DialogTitle,
         DialogContent,
         DialogActions,
         DialogContentText,
         Tabs,
         Tab,
         useTheme,
        }                                     from '@mui/material';
import { RecoApis }                           from 'apis/recoAPI';
import { useGlobalConfig }                    from 'context/GlobalContext';
import { createRecoAction, 
         selectDraftRecos, 
         selectModifiedRecos, 
         selectPublishedRecos, 
         selectTableState, 
         updateModifiedRecosAction
       }                                      from 'store/dataReducers/recommendationsSlice';
import { useDispatch, 
         useSelector 
       }                                      from 'react-redux';
import { ApiErrorInfo }                       from 'types';
import { APIData }                            from 'types/cms/frontendTypes';
import { RecoEditForm 
       }                                      from './RecoEditFormComponent';
import { CMS_DEFAULT_LOCALE, 
         selectCMSPilotList, 
         selectCMSSelectedPilotDocId, 
         selectCMSSelectedPilotRecoScore 
       }                                      from 'store/dataReducers/cmsPilotSlice';
import { CreateRecoRequestData }              from 'types/apis/recoInterfaces';
import { ROUTE_TREE }                         from 'routes/AppRoutes';

export interface CreateOrEditRecoComponentProps {
  editMode: 'create' | 'update'
  recoDocId?: string
}

export const CreateOrEditRecoComponent = (props : CreateOrEditRecoComponentProps) => {

  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const { editMode, recoDocId } = props;
  const [modifiedReco, setModifiedReco]   = useState<APIData<"api::recommendation.recommendation"> | undefined>(undefined);
  const [publishedReco, setPublishedReco] = useState<APIData<"api::recommendation.recommendation"> | undefined>(undefined);
  
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<ApiErrorInfo | undefined>();
  const [isFormDirty, setIsFormDirty] = useState(false);
  
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);
  
  const draftRecos      = useSelector(selectDraftRecos)
  const publishedRecos  = useSelector(selectPublishedRecos)
  const modifiedRecos   = useSelector(selectModifiedRecos)
  const tableState      = useSelector(selectTableState)
  const utilityDocumentId = useSelector(selectCMSSelectedPilotDocId)
  const selectedPilot = useSelector(selectCMSPilotList).find(pilot => pilot.documentId === utilityDocumentId)!
  const recoScore = useSelector(selectCMSSelectedPilotRecoScore)

  const dispatch = useDispatch();

  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  const fetchRecoData = useCallback(() => {

    if (!recoDocId) return;
    const draftReco = draftRecos.find(reco => reco.documentId === recoDocId);
    if (draftReco) {
      setModifiedReco(draftReco);
      setPublishedReco(undefined);
      
    } else {
      const foundModifiedReco  = modifiedRecos.find(reco => reco.documentId === recoDocId);
      const foundPublishedReco = publishedRecos.find(reco => reco.documentId === recoDocId);
      setModifiedReco(foundModifiedReco || foundPublishedReco);
      setPublishedReco(foundPublishedReco);
    }
  }, [recoDocId, draftRecos, modifiedRecos, publishedRecos]);

  useEffect(() => {
    fetchRecoData();
  }, [fetchRecoData]);

  const handleSubmit = async (data: CreateRecoRequestData) => {
    
    let resp;
    setError(undefined);
    
    if (editMode === 'create') {
      resp = await RecoApis.createReco(rc, utilityDocumentId, data);
      if (resp.error || resp.data?.error) {
        setError(resp.error || resp.data?.error);
      } else {
        dispatch(createRecoAction({
          reco : resp.data!.reco,
          recoScore : recoScore
        }));
        rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.EDIT, { id: resp.data!.reco.documentId, replace: true });
      }
    } else {
      resp = await RecoApis.updateRecos(rc, utilityDocumentId, selectedPilot.pilotId, [data as any]);
      if (resp.error || resp.data?.error) {
        let errorMsg = resp.error?.message || resp.data?.error
        if (errorMsg && typeof errorMsg === 'object') {
          errorMsg = JSON.stringify(errorMsg)
        }
        setError(errorMsg);
      } else {
        dispatch(updateModifiedRecosAction({
          recos : resp.data!.recos,
          recoScore : recoScore!
        }));
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (activeTab === newValue) return;
    
    if (isFormDirty) {
      setPendingTabChange(newValue);
      setShowConfirmDialog(true);
    } else {
      setActiveTab(newValue);
    }
  };

  const handleConfirmTabChange = () => {
    if (pendingTabChange !== null) {
      setActiveTab(pendingTabChange);
      setPendingTabChange(null);
    }
    setShowConfirmDialog(false);
  };

  const handleCancelTabChange = () => {
    setPendingTabChange(null);
    setShowConfirmDialog(false);
  };

  if (editMode === 'create') {
    return (
      <RecoEditForm 
        submitError={error}
        onClearError={clearError}
        editMode={editMode} 
        selectedPilot={selectedPilot}
        selectedLocale={CMS_DEFAULT_LOCALE}
        onSubmit={handleSubmit} 
        onFormStateChange={setIsFormDirty}
      />
    );
  }

  return (
    <Box sx={{ height: '100%'}}>
      <Box sx={{ mt: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          textColor="inherit"
          slotProps={{
            indicator: {
              sx: { backgroundColor: theme.palette.text.secondary }
            }
          }}
          sx={{
            '& .Mui-selected': {
              color: theme.palette.text.secondary
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!publishedReco ? 'Draft' : 'Modified'}
              </Box>
            } 
          />
          <Tab 
            disabled={!publishedReco}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Published
              </Box>
            } 
          />
        </Tabs>
      </Box>

      <Box sx={{ height: 'calc(100vh - 110px)', overflow: 'auto' }}>
        {activeTab === 0 && (
          <RecoEditForm 
            submitError={error}
            onClearError={clearError}
            reco={modifiedReco} 
            selectedPilot={selectedPilot}
            selectedLocale={tableState.selectedLocale || CMS_DEFAULT_LOCALE}
            editMode={editMode} 
            onSubmit={handleSubmit} 
            isDraft={!publishedReco}
            onFormStateChange={setIsFormDirty}
          />
        )}
        {activeTab === 1 && (
          <RecoEditForm 
            reco={publishedReco}
            selectedPilot={selectedPilot}
            editMode={editMode} 
            selectedLocale={tableState.selectedLocale || CMS_DEFAULT_LOCALE}
            onSubmit={handleSubmit} 
            isPublished={true}
            onFormStateChange={setIsFormDirty}
          />
        )}
      </Box>

      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelTabChange}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to switch tabs? Your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTabChange}>Cancel</Button>
          <Button onClick={handleConfirmTabChange} color="primary" variant="contained">
            Switch Tabs
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
